import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function SceneCapabilities() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "+=320%",
          scrub: 1,
          pin: true,
        },
      });

      // THINK expands wide, spreads columns
      tl.from(".word-01", { fontStretch: "100%", opacity: 0, yPercent: 20, duration: 0.5 }, 0);
      tl.to(".word-01", {
        letterSpacing: "0.4em",
        opacity: 0.15,
        duration: 1,
      }, 0.6);

      // BUILD comes in using column negative space
      tl.fromTo(".word-02",
        { opacity: 0, yPercent: 80, letterSpacing: "-0.06em" },
        { opacity: 1, yPercent: 0, letterSpacing: "-0.06em", duration: 0.8 },
        0.9);
      tl.from(".column-plane", { scaleY: 0, transformOrigin: "top center", stagger: 0.05, duration: 0.5 }, 0.9);

      tl.to(".word-02", {
        yPercent: -20,
        opacity: 0.15,
        duration: 0.8,
      }, 1.9);

      // Planes collapse into structural planes for SHIP
      tl.to(".column-plane", {
        scaleY: 0.3,
        stagger: 0.03,
        duration: 0.5,
      }, 2.0);

      tl.fromTo(".word-03",
        { opacity: 0, scale: 0.6, yPercent: 30 },
        { opacity: 1, scale: 1, yPercent: 0, duration: 0.8 },
        2.3);

      tl.from(".ship-sub > *", {
        yPercent: 100,
        opacity: 0,
        stagger: 0.08,
        duration: 0.4,
      }, 2.6);
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="scene-caps"
      ref={root}
      data-theme="dark"
      className="relative h-screen w-full overflow-hidden ink-bg grain"
    >
      <div className="absolute top-24 left-6 md:left-10 type-label text-[color:var(--gray)]">
        <div>04 / CAPABILITIES</div>
      </div>
      <div className="absolute top-24 right-6 md:right-10 type-label text-[color:var(--gray)] text-right">
        <div>THREE STATES</div>
      </div>

      {/* columns (negative space) */}
      <div className="absolute inset-0 flex justify-between px-[10vw] pointer-events-none">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="column-plane w-[6vw] h-full bg-[color:var(--dark)]" />
        ))}
      </div>

      {/* THINK */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="word-01 type-display text-[color:var(--paper)]" style={{ fontSize: "22vw", letterSpacing: "-0.06em" }}>
          THINK
        </div>
      </div>

      {/* BUILD */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="word-02 type-display text-[color:var(--paper)]" style={{ fontSize: "22vw" }}>
          BUILD
        </div>
      </div>

      {/* SHIP */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="word-03 type-display text-[color:var(--signal)]" style={{ fontSize: "28vw" }}>
          SHIP
        </div>
      </div>

      {/* description labels */}
      <div className="absolute bottom-32 left-6 md:left-10 max-w-md type-mono text-[color:var(--gray)] text-xs space-y-1">
        <div>01 / THINK</div>
        <div className="text-[color:var(--paper)]">FOLLOW THE PROBLEM BEYOND THE FIRST DRAFT.</div>
      </div>

      <div className="absolute bottom-32 right-6 md:right-10 max-w-md text-right type-mono text-[color:var(--gray)] text-xs space-y-1 ship-sub">
        <div>02 / BUILD → 03 / SHIP</div>
        <div className="text-[color:var(--paper)]">MOVE FROM INTENT TO WORKING SYSTEMS.</div>
        <div className="text-[color:var(--signal)]">PLAN. EXECUTE. VERIFY. DEPLOY.</div>
      </div>

      <div className="absolute bottom-6 left-0 right-0 px-6 md:px-10 flex items-end justify-between">
        <div className="type-label text-[color:var(--gray)]">STATE MACHINE / 04</div>
        <div className="type-label text-[color:var(--gray)]">TYPOGRAPHIC TRANSFORM</div>
      </div>
    </section>
  );
}